import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import Collapse from '@mui/material/Collapse'
import FormControlLabel from '@mui/material/FormControlLabel'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { chooseDBTranslation } from 'utils/translations'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { ISkill } from 'types/levelItemInterfaces'
import { ICertificate, ICompetence } from 'types/cvsInterfaces'

/** @notExported */
interface IDataChipsProps {
  /** The data. */
  data: ISkill[] | ICertificate[] | ICompetence[]
  /** The type. */
  type: 'skill' | 'role' | 'industry' | 'certificate' | 'competence'
}

/**
 * The DataChips component.
 *
 * @param data - The data.
 * @param type - The type.
 * @param color - The color.
 * @returns The DataChips component.
 * @notExported
 */
const DataChips: React.FC<IDataChipsProps> = ({ data, type }) => {
  const { i18n } = useTranslation()

  const [showMore, setShowMore] = useState<boolean>(false)

  /**
   * Gets the display label for a skill, certificate or competence item.
   *
   * @param item - The item to get the label for.
   * @returns The translated label.
   */
  const getLabel = (item: ISkill | ICertificate | ICompetence): string => {
    if (type === 'certificate') {
      return chooseDBTranslation(i18n, item as ICertificate).certificate
    }
    return chooseDBTranslation(i18n, item as ISkill | ICompetence).name
  }

  if (data.length > 10) {
    const newData = [...data]
    const split = newData.splice(0, 10)

    return (
      <>
        <FormControlLabel
          labelPlacement="start"
          control={
            <Checkbox
              data-testid={`checkbox-team-data-chips-toggle-${type}`}
              onChange={() => setShowMore(!showMore)}
              icon={<ExpandMoreIcon />}
              checkedIcon={<ExpandLessIcon />}
              name="showMoreCollapse"
            />
          }
          label={split.map(item => (
            <Chip key={item.id} label={getLabel(item)} variant="outlined" sx={{ mb: 1, mr: 1, borderRadius: 1 }} />
          ))}
          sx={{ m: 0, p: 0 }}
        />
        <Collapse in={showMore}>
          {newData.map(item => (
            <Chip key={item.id} label={getLabel(item)} variant="outlined" sx={{ mb: 1, mr: 1, borderRadius: 1 }} />
          ))}
        </Collapse>
      </>
    )
  } else {
    return data.map(item => (
      <Chip key={item.id} label={getLabel(item)} variant="outlined" sx={{ mb: 1, mr: 1, borderRadius: 1 }} />
    ))
  }
}

export default DataChips
